import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { tagService, type KbTagAvailability, type KbTagDefinition } from "../api/tagService";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * 可用度模式(2026-09-23,陪练「知识问答闯关」):按所选产品线显示每个标签下有多少可出题内容。
 *
 * 为什么:此前选择器列的是全部标签字典,和「选中的产品线下有没有料」无关。测试机实例 ——
 * 场景绑 zhinao + product_knowledge,交集为空,ai 静默忽略标签改从全范围出题,管理员配的时候完全看不出来。
 * 数字口径见 tagService 的 KbTagAvailability 注释(切片数,是上限)。
 */
export interface TagAvailabilityOptions {
  /** 场景绑定的产品线(空 = 全库) */
  productCodes: string[];
  /** 题目数量;可出题内容少于它时告警 */
  minCount?: number;
}

interface TagSelectorProps {
  /** Comma-separated tag values (controlled value) */
  value: string;
  /** Called with updated comma-separated tag values */
  onChange: (value: string) => void;
  /** Optional tenant code override (for super admin managing other tenants) */
  tenantCode?: string;
  /** 传了才开可用度模式;不传 = 原行为(知识库上传页等) */
  availability?: TagAvailabilityOptions;
}

export function TagSelector({ value, onChange, tenantCode, availability }: TagSelectorProps) {
  const { t } = useTranslation();
  const { data: tags = [], isLoading } = useQuery({
    queryKey: ["kb-tags", tenantCode ?? ""],
    queryFn: () => tagService.list(),
  });

  const selected = new Set(
    value
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
  );

  // 排序后进 key:同一组产品/标签勾选顺序不同不该重新请求
  const productKey = availability ? [...availability.productCodes].sort() : [];
  const selectedKey = [...selected].sort();
  const { data: avail } = useQuery({
    queryKey: ["kb-tag-availability", tenantCode ?? "", productKey, selectedKey],
    queryFn: () => tagService.availability(productKey, selectedKey),
    enabled: !!availability,
    // 勾标签会换 key:沿用上一份数字,避免计数闪成空
    placeholderData: keepPreviousData,
  });

  const toggle = (tagValue: string) => {
    const next = new Set(selected);
    if (next.has(tagValue)) {
      next.delete(tagValue);
    } else {
      next.add(tagValue);
    }
    onChange(Array.from(next).join(","));
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("tagSelector.loading")}
      </div>
    );
  }

  if (tags.length === 0) {
    return <p className="text-sm text-muted-foreground py-2">{t("tagSelector.emptyHint")}</p>;
  }

  const publicTags = tags.filter((tag) => tag.category === "public");
  const sceneTags = tags.filter((tag) => tag.category === "scene");
  // 可用度没拿到(未开 / 加载中 / 失败)→ null,标签照原样可选,不瞎置灰
  const counts = availability && avail ? countMap(avail) : null;

  return (
    <div className="space-y-3">
      {publicTags.length > 0 && (
        <TagGroup
          label={t("tagSelector.groupPublic")}
          tags={publicTags}
          selected={selected}
          onToggle={toggle}
          counts={counts}
        />
      )}
      {sceneTags.length > 0 && (
        <TagGroup
          label={t("tagSelector.groupScene")}
          tags={sceneTags}
          selected={selected}
          onToggle={toggle}
          counts={counts}
        />
      )}
      {availability && avail && counts && (
        <AvailabilitySummary
          avail={avail}
          counts={counts}
          tags={tags}
          selected={selected}
          minCount={availability.minCount}
        />
      )}
    </div>
  );
}

function countMap(avail: KbTagAvailability): Map<string, number> {
  return new Map(avail.tags.map((x) => [x.tagValue, x.chunkCount]));
}

function TagGroup({
  label,
  tags,
  selected,
  onToggle,
  counts,
}: {
  label: string;
  tags: KbTagDefinition[];
  selected: Set<string>;
  onToggle: (tagValue: string) => void;
  counts: Map<string, number> | null;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const checked = selected.has(tag.tagValue);
          const count = counts ? (counts.get(tag.tagValue) ?? 0) : null;
          // 已选的零计数标签不禁用 —— 否则管理员连取消勾选都做不到
          const empty = count === 0;
          const disabled = empty && !checked;
          return (
            <label
              key={tag.id}
              className={
                disabled
                  ? "flex items-center gap-1.5 cursor-not-allowed select-none opacity-50"
                  : "flex items-center gap-1.5 cursor-pointer select-none"
              }
              title={empty ? t("tagSelector.availability.emptyHint") : undefined}
            >
              <Checkbox
                checked={checked}
                disabled={disabled}
                onCheckedChange={() => onToggle(tag.tagValue)}
              />
              <span className={empty && checked ? "text-sm text-warning" : "text-sm"}>
                {tag.name}
                {count !== null && (
                  <span className="text-muted-foreground tabular-nums"> · {count}</span>
                )}
              </span>
              {tag.isSystem && (
                <Badge variant="outline" className="text-2xs px-1 py-0">
                  {t("tagSelector.systemBadge")}
                </Badge>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 范围合计 + 告警。口径跟 ai 出题取材走(routes._qa_drill_chunks → list_chunks_for_quiz):
 * 选了标签且命中 → 用命中量;选了标签但一条不中 → ai 忽略标签、从当前范围全部内容出题;没选标签 → 范围全部。
 */
function AvailabilitySummary({
  avail,
  counts,
  tags,
  selected,
  minCount,
}: {
  avail: KbTagAvailability;
  counts: Map<string, number>;
  tags: KbTagDefinition[];
  selected: Set<string>;
  minCount?: number;
}) {
  const { t } = useTranslation();
  const nameOf = new Map(tags.map((x) => [x.tagValue, x.name]));
  const emptySelected = [...selected].filter((v) => (counts.get(v) ?? 0) === 0);
  const tagsMiss = selected.size > 0 && (avail.matched?.chunkCount ?? 0) === 0;
  const effective = selected.size > 0 && !tagsMiss && avail.matched ? avail.matched : avail.total;
  const n = effective.chunkCount;

  const warnings: string[] = [];
  if (tagsMiss && avail.total.chunkCount > 0) {
    warnings.push(t("tagSelector.availability.tagsMissFallback"));
  } else if (emptySelected.length > 0) {
    warnings.push(
      t("tagSelector.availability.selectedEmpty", {
        tags: emptySelected.map((v) => nameOf.get(v) ?? v).join("、"),
      })
    );
  }
  if (n === 0) {
    warnings.push(t("tagSelector.availability.none"));
  } else if (minCount && n < minCount) {
    warnings.push(t("tagSelector.availability.tooFew", { count: n, min: minCount }));
  }

  return (
    <div className="space-y-1.5" data-testid="tag-availability-summary">
      <p className="text-xs text-muted-foreground">
        {t("tagSelector.availability.total", { count: n })}
      </p>
      {warnings.map((w) => (
        <p
          key={w}
          role="alert"
          className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-2 py-1.5 text-xs text-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{w}</span>
        </p>
      ))}
    </div>
  );
}
